import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  ViewChild,
  computed,
  inject,
  input,
  Output
} from '@angular/core';
import { CommonModule, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { LtTranslatePipe } from '@app/pipes/lt-translate.pipe';
import { LtIconComponent } from '@app/components/core/lt-icon/lt-icon.component';
import { parseFaIcon } from '@app/components/core/lt-icon/lt-icon.config';
import type {
  ButtonsConfig,
  ComposableModalConfig,
  DetailConfig,
  HeaderConfig,
  ModalResultProvider
} from '@app/components/modals/config/composable-modal.config';
import { ButtonLayout, DEFAULT_MODAL_CONFIG } from '@app/components/modals/config/composable-modal.config';

@Component({
  selector: 'lt-composable-modal',
  imports: [CommonModule, NgTemplateOutlet, NgComponentOutlet, LtTranslatePipe, LtIconComponent],
  templateUrl: './composable-modal.component.html',
  styleUrl: './composable-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComposableModalComponent {
  private readonly dialogRef = inject(DialogRef<unknown>, { optional: true });
  private readonly dialogData = inject<ComposableModalConfig>(DIALOG_DATA, { optional: true });

  header = input<HeaderConfig>(this.dialogData?.header ?? DEFAULT_MODAL_CONFIG.header);
  detail = input<DetailConfig>(this.dialogData?.detail ?? DEFAULT_MODAL_CONFIG.detail);
  buttons = input<ButtonsConfig>(this.dialogData?.buttons ?? DEFAULT_MODAL_CONFIG.buttons);

  @ViewChild('innerComponentOutlet') innerComponentOutlet: NgComponentOutlet | undefined;

  protected iconClasses = computed(() => {
    const iconName = this.header().icon;
    if (!iconName) return '';

    const faRef = parseFaIcon(iconName);
    if (!faRef) return iconName;

    const [prefix, iconKey] = faRef;
    return `${prefix} fa-${iconKey}`;
  });

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();

  ButtonLayout: typeof ButtonLayout = ButtonLayout;

  onCancel(): void {
    this.dialogRef?.close(false);
  }

  onConfirm(): void {
    const outlet = this.innerComponentOutlet as unknown as { componentInstance?: unknown };
    const instance = outlet?.componentInstance as Partial<ModalResultProvider>;
    const result = typeof instance?.getModalResult === 'function' ? instance.getModalResult() : true;
    this.dialogRef?.close(result);
  }
}
